import { memo, useCallback } from 'react';
import { Typography, styled } from '@mui/material';
import type { CellContext } from '@tanstack/react-table';

import { isUndefined } from 'utils/typeChecks';
import type { StaticSelectionSettings } from 'types/dataHelios';
import type { FormItemUiDefinition } from 'types/formItem';
import { getUiDefinitionTextStyles } from 'utils/styles';

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isClickable' && prop !== 'hasValue',
})<{ isClickable?: boolean; hasValue?: boolean }>(({ isClickable, hasValue, theme }) => ({
  transition: isClickable ? 'all' : 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  ...(isClickable
    ? {
        '&:hover': {
          textDecoration: 'underline',
          cursor: 'pointer',
        },
      }
    : {
        cursor: 'default',
      }),
  color: !hasValue ? `${theme.palette.text.secondary}` : 'inherit',
  fontStyle: !hasValue ? 'italic' : 'normal',
}));

interface Props<T, P> {
  value?: string;
  row: CellContext<T, P>;
  onClick?: (row: T, cell: keyof P) => unknown;
  isClickable?: boolean;
  dataType?: string;
  staticSelection?: StaticSelectionSettings;
  formItemUiDefinition?: FormItemUiDefinition;
}

const TextBase = <T, P>({ value, row, onClick, isClickable, staticSelection, formItemUiDefinition }: Props<T, P>) => {
  const textStyles = getUiDefinitionTextStyles(formItemUiDefinition);

  const handleRowClick = () => {
    if (onClick) {
      onClick(row.row.original, row.column.id as keyof P);
    }
  };

  const hasValue = !isUndefined(value) && String(value).length > 0;

  const getValue = useCallback(
    () => staticSelection?.selectItems?.find((item) => item.key === value)?.value ?? '',
    [staticSelection?.selectItems, value]
  );

  return (
    <StyledTypography
      variant="body1"
      isClickable={isClickable}
      hasValue={hasValue}
      noWrap
      onClick={handleRowClick}
      style={{ ...textStyles, ...(textStyles.fontStyle === 'italic' ? { paddingRight: '0.2em' } : {}) }}>
      {hasValue ? getValue() : ''}
    </StyledTypography>
  );
};

export const StaticSelectionHelios = memo(TextBase) as typeof TextBase;
